import { useEffect, useState } from "react";
import axios from "axios";
import { MessageSquare, Send, Star } from "lucide-react";
import { API_BASE_URL } from "../lib/site";

const API = API_BASE_URL;

function formatDisplayDate(value, options = { day: "numeric", month: "long", year: "numeric" }) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString("en-IN", options);
  } catch {
    return "";
  }
}

function ReplyBlock({ text }) {
  if (!text) return null;
  return (
    <div className="mt-4 border-l-2 border-[#002FA7] bg-blue-50/60 px-4 py-3">
      <p className="text-[10px] tracking-[0.14em] uppercase font-bold text-[#002FA7] mb-1">
        Reply from Niharika
      </p>
      <p className="text-sm text-[#33507A] leading-relaxed">{text}</p>
    </div>
  );
}

export function StarRow({ value, interactive = false, onChange, size = 16 }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) =>
        interactive ? (
          <button
            key={star}
            type="button"
            onClick={() => onChange?.(star)}
            className="cursor-pointer"
            aria-label={`${star} star rating`}
          >
            <Star
              size={size}
              className={star <= value ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#D1D5DB]"}
            />
          </button>
        ) : (
          <span key={star}>
            <Star
              size={size}
              className={star <= value ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#D1D5DB]"}
            />
          </span>
        )
      )}
    </div>
  );
}

export function CommentList({
  comments,
  dataTestId = "comments-list"
}) {
  if (!comments.length) {
    return null;
  }

  return (
    <div className="space-y-4" data-testid={dataTestId}>
      {comments.map((comment) => (
        <div key={comment.id} className="bg-white border border-[#E5E7EB] p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-[#F3F4F6] flex items-center justify-center text-xs font-bold text-[#4B5563] flex-shrink-0">
              {comment.author_name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#0A0A0A]">{comment.author_name}</p>
              <p className="text-xs text-[#9CA3AF] mb-2">
                {formatDisplayDate(comment.created_at)}
              </p>
              <p className="text-sm text-[#4B5563] leading-relaxed whitespace-pre-wrap">
                {comment.content}
              </p>
              <ReplyBlock text={comment.reply_text} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ReviewList({
  reviews,
  emptyState = "No approved reviews are live yet. You can be the first to leave one below.",
  dataTestId = "reviews-list"
}) {
  if (!reviews.length) {
    return (
      <div className="border border-dashed border-[#D1D5DB] bg-white p-8 text-sm text-[#6B7280]">
        {emptyState}
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid={dataTestId}>
      {reviews.map((review) => (
        <div key={review.id} className="border border-[#E5E7EB] bg-white p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <p className="font-semibold text-sm text-[#0A0A0A]">{review.author_name}</p>
              <p className="text-xs text-[#9CA3AF]">
                {formatDisplayDate(review.created_at, {
                  day: "numeric",
                  month: "short",
                  year: "numeric"
                })}
              </p>
            </div>
            <StarRow value={review.rating} />
          </div>
          <p className="text-sm text-[#4B5563] leading-relaxed whitespace-pre-wrap">{review.content}</p>
          <ReplyBlock text={review.reply_text} />
        </div>
      ))}
    </div>
  );
}

export function CommentForm({
  subjectType,
  subjectId,
  title = "Leave a Response",
  description = null,
  placeholder = "Share your thoughts...",
  buttonText = "Submit Comment",
  successMessage = "Your comment has been submitted for review. Thank you!",
  onSubmitted
}) {
  const [form, setForm] = useState({ author_name: "", author_email: "", content: "" });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus("");
    try {
      await axios.post(`${API}/comments`, {
        ...form,
        subject_type: subjectType,
        subject_id: subjectId
      });
      setStatus("success");
      setForm({ author_name: "", author_email: "", content: "" });
      onSubmitted?.();
    } catch {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3 className="font-['Outfit'] font-semibold text-[#0A0A0A] text-2xl mb-2">{title}</h3>
      {description && <div className="text-sm text-[#6B7280] leading-relaxed mb-5">{description}</div>}
      {status === "success" ? (
        <div className="border border-[#002FA7]/20 bg-blue-50 p-4 text-sm text-[#002FA7]">
          {successMessage}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="comment-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#0A0A0A] mb-1.5 uppercase tracking-wide">
                Name
              </label>
              <input
                type="text"
                required
                value={form.author_name}
                onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:border-[#002FA7]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#0A0A0A] mb-1.5 uppercase tracking-wide">
                Email
              </label>
              <input
                type="email"
                required
                value={form.author_email}
                onChange={(e) => setForm({ ...form, author_email: e.target.value })}
                className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:border-[#002FA7]"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] mb-1.5 uppercase tracking-wide">
              Comment
            </label>
            <textarea
              required
              rows={5}
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:border-[#002FA7] resize-none"
              placeholder={placeholder}
            />
          </div>
          {status === "error" && (
            <p className="text-red-500 text-sm">Something went wrong. Please try again.</p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="bg-[#002FA7] text-white px-6 py-3 text-sm font-semibold hover:bg-[#001D66] transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <Send size={14} /> {loading ? "Sending..." : buttonText}
          </button>
        </form>
      )}
    </div>
  );
}

export function ReviewForm({
  subjectType,
  subjectId,
  title = "Leave a review",
  description = "Reviews are moderated first and published only after approval.",
  placeholder = "Share what stood out, what was useful, or what you would debate.",
  buttonText = "Submit Review",
  successMessage = "Thank you. Your review has been submitted for approval.",
  ratingLabel = "Rating",
  contentLabel = "Review",
  onSubmitted
}) {
  const [form, setForm] = useState({
    author_name: "",
    author_email: "",
    rating: 5,
    content: ""
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus("");
    try {
      await axios.post(`${API}/reviews`, {
        ...form,
        subject_type: subjectType,
        subject_id: subjectId
      });
      setStatus("success");
      setForm({ author_name: "", author_email: "", rating: 5, content: "" });
      onSubmitted?.();
    } catch {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {title ? <h3 className="font-['Outfit'] font-semibold text-[#0A0A0A] text-2xl mb-2">{title}</h3> : null}
      {description ? <p className="text-sm text-[#6B7280] leading-relaxed mb-5">{description}</p> : null}
      {status === "success" ? (
        <div className="border border-[#002FA7]/20 bg-blue-50 p-4 text-sm text-[#002FA7]">
          {successMessage}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="review-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#0A0A0A] mb-1.5 uppercase tracking-wide">
                Name
              </label>
              <input
                required
                value={form.author_name}
                onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:border-[#002FA7]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#0A0A0A] mb-1.5 uppercase tracking-wide">
                Email
              </label>
              <input
                type="email"
                required
                value={form.author_email}
                onChange={(e) => setForm({ ...form, author_email: e.target.value })}
                className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:border-[#002FA7]"
              />
            </div>
          </div>
          <div>
            {ratingLabel ? (
              <label className="block text-xs font-semibold text-[#0A0A0A] mb-1.5 uppercase tracking-wide">
                {ratingLabel}
              </label>
            ) : null}
            <StarRow value={form.rating} interactive onChange={(rating) => setForm({ ...form, rating })} />
          </div>
          <div>
            {contentLabel ? (
              <label className="block text-xs font-semibold text-[#0A0A0A] mb-1.5 uppercase tracking-wide">
                {contentLabel}
              </label>
            ) : null}
            <textarea
              rows={5}
              required
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:border-[#002FA7] resize-none"
              placeholder={placeholder}
            />
          </div>
          {status === "error" && (
            <p className="text-sm text-red-500">Something went wrong. Please try again.</p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="bg-[#002FA7] text-white px-6 py-3 text-sm font-semibold hover:bg-[#001D66] transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <Send size={14} /> {loading ? "Submitting..." : buttonText}
          </button>
        </form>
      )}
    </div>
  );
}

export function SubjectFeedbackSection({
  subjectType,
  subjectId,
  heading = "Responses",
  placeholder = "Share your thoughts..."
}) {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    if (!subjectType || !subjectId) return;

    const query = new URLSearchParams({
      subject_type: subjectType,
      subject_id: subjectId
    }).toString();

    axios.get(`${API}/reviews?${query}`)
      .then((reviewRes) => {
        setReviews(reviewRes.data);
      })
      .catch(() => {
        setReviews([]);
      });
  }, [subjectType, subjectId]);

  const refresh = () => {
    const query = new URLSearchParams({
      subject_type: subjectType,
      subject_id: subjectId
    }).toString();

    axios.get(`${API}/reviews?${query}`).then((res) => setReviews(res.data)).catch(() => {});
  };

  return (
    <section className="border-t border-[#E5E7EB] bg-[#F9FAFB]">
      <div className="max-w-3xl mx-auto px-6 lg:px-0 py-12">
        <h2 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A] flex items-center gap-2 mb-8">
          <MessageSquare size={20} className="text-[#002FA7]" />
          {heading}
          {reviews.length > 0 && <span className="text-sm font-normal text-[#6B7280]">({reviews.length})</span>}
        </h2>
        {reviews.length > 0 ? (
          <div className="mb-8">
            <ReviewList reviews={reviews} />
          </div>
        ) : null}
        <div className="bg-white border border-[#E5E7EB] p-6">
          <ReviewForm
            subjectType={subjectType}
            subjectId={subjectId}
            title=""
            description=""
            placeholder={`${placeholder}\nI read everything before it goes live.`}
            buttonText="Send"
            successMessage="Thank you for your comment. It is pending to go live."
            ratingLabel=""
            contentLabel=""
            onSubmitted={refresh}
          />
        </div>
      </div>
    </section>
  );
}

export function HomepageConversationSection() {
  const [comments, setComments] = useState([]);

  const load = () => {
    const query = new URLSearchParams({
      subject_type: "site",
      subject_id: "home"
    }).toString();

    axios.get(`${API}/comments?${query}`).then((res) => setComments(res.data)).catch(() => setComments([]));
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <section className="border-t border-[#E5E7EB] bg-[#F9FAFB]" data-testid="conversations-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 sm:py-20 grid lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,420px)] gap-10">
        <div>
          <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-2">
            Conversations
          </p>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-medium text-[#0A0A0A] mb-4">
            What this work leads to
          </h2>
          <div className="text-[#4B5563] leading-relaxed max-w-xl space-y-4 mb-8">
            <p>Not everything here is meant to be agreed with.</p>
            <p>
              If something made you pause, question your approach, or see growth differently
              {" "}
              — that’s the point.
            </p>
            <p>Some of the most useful thinking comes from what happens after reading.</p>
          </div>
          <CommentList
            comments={comments}
            emptyState="No approved conversations are live yet. You can start one below."
            dataTestId="homepage-conversations-list"
          />
        </div>

        <div className="border border-[#E5E7EB] bg-white p-6">
          <CommentForm
            subjectType="site"
            subjectId="home"
            title="Start a conversation"
            description={
              <>
                <p>If something here sparked a thought, disagreement, or a different perspective — share it.</p>
                <p className="mt-2">I read everything before it goes live.</p>
              </>
            }
            placeholder="What did you find useful, questionable, or worth debating?"
            buttonText="Send"
            successMessage="Thank you. Your conversation has been sent for review."
            onSubmitted={load}
          />
        </div>
      </div>
    </section>
  );
}
